import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { BatchFolder } from "../../pages/playground/types";
import { LoadingButton } from "@mui/lab";
import { getBatchesFolders, updateBrandGDriveFolder } from "../../api/batches";

interface GDriveFolderPickerDialogProps {
  open: boolean;
  brandId: string;
  brandName: string;
  onDone: () => void;
  onCancel: () => void;
}

export const GDriveFolderPickerDialog: React.FC<
  GDriveFolderPickerDialogProps
> = ({ open, brandId, brandName, onDone, onCancel }) => {
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [folders, setFolders] = useState<BatchFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch folders each time the dialog is opened
  useEffect(() => {
    if (!open) return;
    const fetchFolders = async () => {
      try {
        setLoading(true);
        const resp = await getBatchesFolders();
        setFolders(resp.data.data.batches_folders || []);
      } catch (e) {
        console.error("Error fetching GDrive folders", e);
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedFolder("");
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedFolder) return;
    try {
      setSaving(true);
      await updateBrandGDriveFolder({
        brandId,
        gdrive_folder_id: selectedFolder,
      });
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={onCancel}
    >
      <DialogTitle>Select Google Drive folder for the brand</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The new Google Drive organization requires each brand to have its own
          dedicated folder. Select the folder for the brand "{brandName}".
        </Typography>

        <FormControl fullWidth>
          <InputLabel id="gdrive-folder-label">
            Google Drive Folder
          </InputLabel>
          <Select
            labelId="gdrive-folder-label"
            value={selectedFolder}
            label="Cartella"
            onChange={(e) => setSelectedFolder(e.target.value as string)}
          >
            {folders.map((folder) => (
              <MenuItem key={folder.id} value={folder.id}>
                {folder.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving || loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleConfirm}
          disabled={!selectedFolder || saving || loading}
          loading={saving || loading}
          variant="contained"
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
